// deskFilters.js — ONE DECLARATION PER DESK FILTER.
//
// Owner, 2026-08-28: "we need to design side panel once, I don't want to work on it every single
// time something adds on it."
//
// ═══ WHY THIS EXISTS ═══
//
// Adding one filter to the Desk currently costs ELEVEN edits, and every one of them is a place to
// forget. Measured on `balcony_count`, shipped today:
//   ADV_DEFAULTS · ADV_ARRAY_KEYS · deskUrl's key map · core.js transport · keywordEditors ·
//   deskFeed's filters-active test · deskFeed's server-only set · the facet body · rangeChip ·
//   GRAMMAR_ORDER · i18n ×2
//
// The failure mode is never a crash. It is a HALF-EXISTING KEY: the filter works on the live
// request and vanishes on refresh, or lights a chip nothing can clear, or filters correctly while
// the URL forgets it. That shape has now been recorded FOUR times in this codebase, and twice
// today alone — `districtOwnCodes` missing from the committed list, and `featureStates` missing
// from the URL serializer.
//
// The SERVER solved this in 2026-08-01 (B261): `lib/search_filters.js` is one declaration for all
// 62 server-side filters, and its header reads "1. Add one entry below." Parser, gate, RPC body
// and both fail lists are DERIVED from it. Six hand-maintained lists became one.
//
// The Desk never got its equivalent, and the backlog has named that the bug GENERATOR since
// 2026-08-09 (B607). This is that equivalent.
//
// ═══ WHAT IS DERIVED FROM ONE ENTRY ═══
//
//   defaults()        the initial value in ADV_DEFAULTS
//   arrayKeys()       which keys carry array semantics
//   committedKeys()   which survive a commit / refresh / share
//   urlPairs()        both halves of the URL — write AND parse
//   wire()            the query parameter the server registry expects
//   serverOnly()      which the loaded feed cannot answer locally
//   activeIn()        whether a filter set is non-default
//
// A NEW FILTER IS ONE ROW. Nothing below needs a second edit to make it committed, shareable,
// clearable, or sent.
//
// ═══ SCOPE, STATED HONESTLY ═══
//
// ✅ WIRED, 2026-08-28. All twelve sites now DERIVE from this file: ADV_DEFAULTS and
// ADV_ARRAY_KEYS (IntakeTab), COMMITTED_FILTER_KEYS and ARRAY_KEYS (deskSearchState),
// SERVER_ONLY_ADV_KEYS (deskFeed), F2P and both halves of the URL (deskUrl), and the request
// (core.js). A filter added here alone is committed, canonicalised as a set, written to the
// URL, parsed back and sent — asserted by the probe test, which is the only test in the suite
// that can tell a live mechanism from two lists that merely agree.
//
// ⚠️ THE COUNT ABOVE SAID ELEVEN AND IT WAS TWELVE. `ARRAY_KEYS` in deskSearchState was not on
// the list, and it is not a soft omission: a registered set missing from it is canonicalised by
// `String(v)` and throws on the next `.join`. It was found by wiring the other eleven and
// watching a probe filter fail. Assume this list is still short by one.
//
// STILL NOT DONE, deliberately: this does not RENDER the controls, and the hand-written
// literals remain beside the derived unions rather than being deleted. Both are the same
// judgement — the panel lives in a 14,000-line file that has been to its error boundary twice,
// and the literals carry the reasons each key exists, several written the day it was found
// missing. Removing the duplication would cost the documentation and buy nothing: the union
// already makes a registered key authoritative, and the parity tests keep the pairs honest.

/**
 * kind:
 *   'set'    — multi-select of codes/ids (array)
 *   'range'  — a min/max pair (declares both keys)
 *   'flag'   — boolean
 *   'value'  — single scalar
 *
 * Every entry MUST carry `wire` (the server registry's queryKey) and `url` (the hash key), because
 * those are the two lists a hand-written filter forgets. A filter that genuinely should not travel
 * declares `wire: null` explicitly, so the omission is a decision rather than an oversight.
 */
import searchFilterSpec from '../../core/search-filter-spec.js'
function excludeRowsFromSpec() {
  return searchFilterSpec.filters.filter(f => f.exclude && f.client).map(f => ({
    key: f.exclude.state, kind: f.kind === 'flag' ? 'flag' : 'set', layer: f.layer,   // a flag's ✕ half is a flag (topFloorNot), a set's is a set
    wire: f.exclude.wire.length === 1 ? f.exclude.wire[0] : f.exclude.wire,
    url: f.exclude.url.length === 1 ? f.exclude.url[0] : f.exclude.url,
    label: f.label, serverOnly: true, excludeOf: f.key,
    note: 'derived from the spec: the ✕ half of ' + f.key + ' (B1213); NOT the feature :no operator',
  }))
}
export const DESK_FILTERS = [
  // ── Location ───────────────────────────────────────────────────────────────────────────────
  {
    key: 'complexIds', kind: 'set', layer: 'location',
    wire: 'complexes', url: 'complexes', label: 'desk_facet_complex',
    // 332 developments, 1,874 properties. The filter has been complete end-to-end since 0729 —
    // committed, in the URL, pushed down to SQL — and reachable ONLY by clicking a suggestion in
    // the search box. It is the one filter in the system that works and has no control.
    note: 'complex identity; no panel control existed before this registry',
  },
  {
    key: 'streetIds', kind: 'set', layer: 'location',
    wire: 'streets', url: 'streets', label: 'desk_facet_street',
    note: 'depends on a picked district; StreetFacetList owns the control',
  },
  {
    key: 'metroIds', kind: 'set', layer: 'location',
    wire: 'metro', url: 'metro', label: 'desk_facet_metro',
    // ⚠️ Its counts are corpus-wide BY DESIGN and must not move when a district is picked — a
    // station is a city-wide landmark. Two adjacent numbers in this panel obey opposite rules.
    note: 'counts are deliberately district-blind',
  },

  // ── Size ───────────────────────────────────────────────────────────────────────────────────
  {
    key: 'balconyCount', kind: 'range', layer: 'size',
    wire: ['balcony_count_min', 'balcony_count_max'], url: ['balcMin', 'balcMax'],
    label: 'desk_facet_balcony_count', serverOnly: true, vacuousZeroMin: true,
    // The control is PILLS, not a slider, and that is the sanity bound: the count runs to a
    // nonsense 58 from extraction errors, so a slider would take its ceiling from that and squash
    // every real answer into the first 7% of the track. "4+" holds the 58 honestly.
    // ⚠️ There is no "0" pill in the count: balcony_count never stores 0 — a count is only
    // extracted when a balcony exists. Zero balconies is the FEATURE's absent state (8,101
    // properties), which is a different key.
    note: 'pills 1/2/3/4+; zero lives on the feature, not the count',
  },

  // -- Location, continued ------------------------------------------------------------------
  //
  // MIGRATION BATCH 1 (2026-09-03). These five were hand-wired at twelve sites each and are now
  // declared once. Every `wire` and `url` below was READ OFF the hand-written code, never
  // inferred -- deskUrl's put() calls and core.js's q.set() calls. A guessed key does not fail
  // loudly; it produces a filter that narrows the request and vanishes on refresh, which is the
  // exact half-existing key this file exists to stop.
  // ⚠️ Verified before declaring: none of the five carries a conditional in core.js that the
  // derived loop would ignore. `agentIds` DOES (a scope === 'all' guard) and is deliberately not
  // in this batch; `intents`/`kinds` send a scalar for one pick and intents=/kinds= for several (3832),
  // which this schema cannot express yet. Both need the registry to learn a new idea first.
  {
    key: 'districtCodes', kind: 'set', layer: 'location',
    wire: 'district_codes', url: 'district', label: 'desk_facet_district',
    // Multi-district since 2026-07-16, OR semantics server-side (0100). NOT serverOnly: the feed
    // rows carry a district, so a loaded page can answer this locally.
    // ⚠️ Its counts OVERLAP and must never be summed -- a disputed property is counted under
    // BOTH candidate districts (0364). That is a correctness choice, not a rounding error.
    note: 'OR across codes; counts overlap by design',
  },
  {
    key: 'districtOwnCodes', kind: 'set', layer: 'location',
    wire: 'district_own_codes', url: 'district_own', label: 'desk_facet_district',
    // 0363/0372 -- "Saburtalo and NOTHING under it". A SECOND array rather than a mode flag,
    // because a flag could not express "Vake with its neighbourhoods, OR Saburtalo exactly".
    // 📌 THIS KEY IS WHY THE REGISTRY EXISTS. It is the recorded half-existing key: absent
    // from the committed list, so it narrowed the live request and was stripped on refresh while
    // its chip still claimed it. Declaring it here makes that fix structural instead of
    // remembered.
    note: 'the raion own bucket, children excluded',
  },

  // -- Identity ------------------------------------------------------------------------------
  {
    key: 'subtypes', kind: 'set', layer: 'identity',
    wire: 'subtypes', url: 'subtypes', label: 'desk_facet_subtype',
    serverOnly: true,
    // B265 (0359/v31). A real array param server-side, unlike `kind`, which is a scalar with
    // single-select pushdown only -- so every selected subtype travels.
    // serverOnly for featureCodes' structural reason: subtype is a CLAIM (current_claim), the
    // feed rows carry none of it, and "no claim" is a three-valued shelf a client cannot fake
    // without hiding real supply.
    note: 'array param; kind is the scalar one',
  },

  // -- Provenance, continued -----------------------------------------------------------------
  {
    key: 'sources', kind: 'set', layer: 'provenance',
    wire: 'sources', url: 'sources', label: 'desk_facet_source',
    // The portal an advert is LISTED on -- distinct from `addedHow`, which is how it entered our
    // book. The layer comment below already names this filter as that layer's other member.
    // 📌 core.js carries a warning about this exact key worth keeping visible: the parameter
    // was once absent from the request while the server side was intact, so ticking Korter on
    // browse returned the whole corpus. "If a filter looks dead, check that the client SENDS it
    // before concluding anything about the server."
    note: 'portal of the advert; any member of the cluster',
  },
  // -- Seller / contact (core 1681 listing_seller_v1 · 1685 search · server search_filters) -----
  // Owner 2026-09-21: channel (Sources above), SELLER (whose property) and CONTACT (who handles it) are three
  // separate questions — never one dropdown mixing Developer / Agent / Owner. An agent is ALWAYS a representative;
  // an unknown principal stays unknown; inferred values count and the card labels them "likely". Include-only (no
  // ✕ half). Within a filter the values OR; across filters AND, on the same advert; a card passes if any advert does.
  {
    key: 'sellerKinds', kind: 'set', layer: 'provenance',
    wire: 'seller_kinds', url: 'seller', label: 'desk_facet_seller_whose',
    serverOnly: true,
    note: 'whose property: private_owner / developer / bank / other_company / unknown',
  },
  {
    key: 'sellerIds', kind: 'set', layer: 'provenance',
    wire: 'seller_ids', url: 'seller_id', label: 'desk_facet_seller_whose',
    serverOnly: true,
    note: 'a named developer or bank inside the Seller filter (party ids); OR-ed with sellerKinds',
  },
  {
    key: 'contactRoles', kind: 'set', layer: 'provenance',
    wire: 'contact_roles', url: 'contact', label: 'desk_facet_contact',
    serverOnly: true,
    note: 'who handles it: owner_direct / agent / company_rep / unknown',
  },
  {
    key: 'agentRepresents', kind: 'set', layer: 'provenance',
    wire: 'agent_represents', url: 'agent_for', label: 'desk_facet_agent_represents',
    serverOnly: true,
    note: 'whom the agent acts for; meaningful with contact=agent',
  },

  // -- Operational ---------------------------------------------------------------------------
  {
    key: 'restrictions', kind: 'set', layer: 'operational',
    wire: 'restrictions', url: 'restrict', label: 'desk_facet_restrictions',
    serverOnly: true,
    // B177 -- entries are "<code>:<hide|allow>", a polarity idiom rather than a bare list.
    // ⚠️ THE URL KEY IS `restrict`, NOT `restrictions`. State key, wire key and url key all
    // differ here, which is exactly the shape a hand-written filter gets right in two places and
    // wrong in the third.
    // The MODE is the server's policy key: what each mode means lives in the versioned
    // search_policy table, so nothing client-side re-implements it and a future ruling is a row.
    note: 'code:polarity entries; mode resolved server-side',
  },

  // -- Size / Budget, migration batch 2 (2026-09-03) ------------------------------------------
  //
  // All six ranges travel through core.js's `numPairs`, which is a PLAIN unguarded loop
  // (`for (const [k, v] of numPairs) if (v !== '' && v != null) q.set(k, v)`), so the derived
  // transport reproduces it exactly. Checked before declaring, because two neighbours in the
  // same function are NOT plain and are deliberately absent from this batch:
  //   currency     -- sent only alongside a price bound
  //   moveInHard   -- sent only alongside moveInBy ("a modifier with nothing to modify")
  // Declaring either would make the client send a parameter it currently withholds.
  //
  // ⚠️ `roomsSet` / `bedroomsSet` are also absent and are NOT simple: they are SETS that
  // wire as a min/max envelope (`env()`, with 5 meaning "5 or more" and therefore an OPEN max)
  // PLUS, since F-090, an optional `num_sets` payload carrying the set itself whenever the
  // selection is non-contiguous. A `set` entry would send the wrong shape and a `range` entry
  // would not match the state keys. They need the schema to learn "set that wires as a range,
  // and additionally as a set when it has a gap" — two wire keys from one state key, which is
  // why this stays hand-written in `applySearchFilterParams` rather than being declared here.
  {
    key: 'price', kind: 'range', layer: 'budget',
    wire: ['price_min', 'price_max'], url: ['priceMin', 'priceMax'],
    label: 'desk_facet_price',
    note: 'currency is a separate key and is deliberately still hand-written',
  },
  {
    // core 3872 (owner 2026-10-24): price per m², in the price's currency (searchWire sends `currency` with it too).
    // serverOnly: the loaded feed cannot judge it — a row's area may live in facts the card does not carry.
    key: 'ppm', kind: 'range', layer: 'budget', serverOnly: true,
    wire: ['ppm_min', 'ppm_max'], url: ['ppmMin', 'ppmMax'],
    label: 'desk_facet_ppm',
    note: 'price per m², the price currency; a key of p_num_sets (RPC at 100 arguments)',
  },
  {
    key: 'area', kind: 'range', layer: 'size',
    wire: ['area_min', 'area_max'], url: ['areaMin', 'areaMax'],
    label: 'desk_facet_area',
    note: 'the BUILDING; landArea is the plot and a different key',
  },
  {
    key: 'landArea', kind: 'range', layer: 'size',
    wire: ['land_area_min', 'land_area_max'], url: ['landAreaMin', 'landAreaMax'],
    label: 'desk_facet_land_area',
    // v29 (0345). Absent params are null server-side, so a client that never sets them behaves
    // exactly as before -- which is why this could ship without a migration of existing URLs.
    note: 'the plot, distinct from area',
  },
  {
    key: 'floor', kind: 'range', layer: 'size',
    wire: ['floor_min', 'floor_max'], url: ['floorMin', 'floorMax'],
    label: 'desk_facet_floor',
    note: 'which floor the flat is on',
  },
  {
    key: 'floorsTotal', kind: 'range', layer: 'size',
    wire: ['floors_total_min', 'floors_total_max'], url: ['floorsTotalMin', 'floorsTotalMax'],
    label: 'desk_facet_floors_total',
    // v6 (0105, audit #8): bathrooms and floorsTotal were the last two panel filters that never
    // reached the server. Client-only since 0048, so once demand-driven loading shipped they
    // only ever saw loaded pages, and a fetch ran FULLY UNFILTERED whenever either was the only
    // active constraint. That is the same class of silent wrongness this registry removes.
    note: 'how many floors the building has',
  },
  {
    key: 'bathrooms', kind: 'range', layer: 'size',
    wire: ['bathrooms_min', 'bathrooms_max'], url: ['bathMin', 'bathMax'],
    label: 'desk_facet_bathrooms',
    // ⚠️ THE URL KEYS ARE ABBREVIATED -- bathMin/bathMax, not bathroomsMin/bathroomsMax. The
    // same divergence as `restrictions` -> `restrict` in batch 1, and the same reason it is
    // early in the migration: a key that differs in exactly one of its three forms is the one a
    // hand-written filter gets wrong.
    note: 'url keys are abbreviated; wire keys are not',
  },

  // -- Tenancy terms -------------------------------------------------------------------------
  {
    key: 'advanceMaxMonths', kind: 'value', layer: 'operational',
    wire: 'advance_max_months', url: 'advanceMax', label: 'desk_facet_advance',
    serverOnly: true,
    // ⚠️ URL key abbreviated again (advanceMax). serverOnly per the hand-written literal list.
    // Its neighbour moveInBy/moveInHard is NOT here: moveInHard rides only alongside a date.
    note: 'months of advance rent; the move-in pair stays hand-written',
  },

  // -- Flags, migration batch 3 (2026-09-03) -------------------------------------------------
  //
  // 🔴 THESE COULD NOT BE DECLARED UNTIL THE SCHEMA LEARNED WHAT A FLAG IS, and the reason
  // is worth keeping because it is invisible: a boolean on the scalar path serialises as the
  // literal "false", while the parse side tests `=== '1'`. Declaring these before urlFlagPairs()
  // existed would have written `topFloor=false` into every URL, the hand-written put() would
  // have pushed a SECOND `topFloor=1`, parse would have taken the first, and the filter would
  // have been silently dropped on every refresh and every shared link. deskUrl's own comment has
  // carried that warning since the file was written; this batch is that warning made structural.
  //
  // ⚠️ TWO FLAGS ARE DELIBERATELY ABSENT, both CONDITIONAL -- a modifier with nothing to
  // modify:
  //   moveInHard  rides only alongside moveInBy (checked in core.js)
  //   keepMine    rides only alongside exposureMax -- and this one nearly slipped through.
  //               Its conditional is NOT in core.js, so checking the transport (which is how
  //               moveInHard was caught) does not find it: `serializeSearch({keepMine:true})`
  //               emits nothing at all, while `{keepMine:true, exposureMax:2}` emits
  //               `keepMine=1`. The stripping happens UPSTREAM of the URL layer.
  // 📌 So the rule for this migration is wider than "read core.js": a filter can be
  // conditional at ANY layer, and the only reliable test is to serialise it ALONE and see
  // whether it survives.
  {
    key: 'topFloor', kind: 'flag', layer: 'size',
    wire: 'top_floor', url: 'topFloor', label: 'desk_facet_top_floor',
    note: 'is the flat on the building top floor',
  },
  {
    key: 'firstFloor', kind: 'flag', layer: 'size',
    wire: 'first_floor', url: 'firstFloor', label: 'desk_facet_first_floor',
    note: 'owner 2026-09-05: first floor (floor = 1) beside top floor; both carry a ✕ half via the spec',
  },
  {
    key: 'noDescription', kind: 'flag', layer: 'quality',
    wire: 'no_description', url: 'noDesc', label: 'desk_facet_no_description',
    // ⚠️ URL key abbreviated (noDesc) while the wire key is not -- the batch-1 shape again.
    note: 'adverts with no description text at all',
  },
  {
    key: 'missingFacts', kind: 'flag', layer: 'quality',
    wire: 'missing_facts', url: 'missingFacts', label: 'desk_facet_missing_facts',
    note: 'adverts missing the facts the card needs',
  },

  // -- Leadwork and intelligence, migration batch 4 (2026-09-03) -----------------------------
  //
  // 📌 EVERY URL KEY BELOW WAS DISCOVERED BY THE LONE-SURVIVAL TEST, not by reading: serialise
  // the filter by itself and see which param appears. That is the rule batch 3 earned after
  // `keepMine` silently emitted nothing, and running it across all 25 remaining candidates paid
  // for itself immediately -- it found four keys whose URL form diverges from their state name
  // (ageMinDays -> ageMin, ageMaxDays -> ageMax, ownerEngaged -> engaged,
  // ownerPlatforms -> ownerPlat) and five that DROP when set alone and are therefore still
  // conditional and still hand-written: ageKinds, streetNearbyM, currency, moveInHard, dateField.
  //
  // ⚠️ `seen` also survives alone and is STILL NOT HERE. Its transport validates the value
  // against a four-mode whitelist before sending; the derived loop would send anything the state
  // happened to hold. Surviving the URL is necessary and not sufficient.
  {
    key: 'exposureMax', kind: 'value', layer: 'operational',
    wire: 'exposure_max', url: 'exposureMax', label: 'desk_facet_exposure',
    serverOnly: true,
    note: 'at-most rung on the exposure ladder',
  },
  {
    key: 'spread', kind: 'value', layer: 'intelligence',
    wire: 'spread', url: 'spread', label: 'desk_facet_spread',
    serverOnly: true,
    note: 'how widely the owner has posted it',
  },
  {
    key: 'sellerType', kind: 'value', layer: 'intelligence',
    wire: 'seller_type', url: 'sellerType', label: 'desk_facet_seller_type',
    serverOnly: true,
    note: 'owner or agency, by the contact behind the advert',
  },
  {
    key: 'claimsLt', kind: 'value', layer: 'operational',
    wire: 'claims_lt', url: 'claimsLt', label: 'desk_facet_claims',
    // ⚠️ ZERO IS A REAL VALUE here -- the owner's "None" case, and the single most likely
    // number an agent types. The derived transport guards on `!== '' && != null && !== false`,
    // and 0 passes all three (0 !== false is true), so it survives. A falsiness check anywhere
    // on this path would silently drop exactly the common case.
    note: 'fewer than N live claims',
  },
  {
    key: 'claimsGt', kind: 'value', layer: 'operational',
    wire: 'claims_gt', url: 'claimsGt', label: 'desk_facet_claims',
    note: 'more than N live claims',
  },
  {
    key: 'ageMinDays', kind: 'value', layer: 'intelligence',
    wire: 'age_min_days', url: 'ageMin', label: 'desk_facet_age',
    note: 'url key abbreviated to ageMin; wire key is not',
  },
  {
    key: 'ageMaxDays', kind: 'value', layer: 'intelligence',
    wire: 'age_max_days', url: 'ageMax', label: 'desk_facet_age',
    note: 'url key abbreviated to ageMax; wire key is not',
  },
  {
    key: 'viewsMin', kind: 'value', layer: 'intelligence',
    wire: 'views_min', url: 'viewsMin', label: 'desk_facet_views',
    note: 'portal view count floor',
  },
  {
    key: 'promoted', kind: 'value', layer: 'intelligence',
    wire: 'promoted', url: 'promoted', label: 'desk_facet_promoted',
    note: 'VIP / promoted placement on the portal',
  },
  {
    key: 'ownerEngaged', kind: 'value', layer: 'intelligence',
    wire: 'owner_engaged', url: 'engaged', label: 'desk_facet_owner_engaged',
    note: 'url key is engaged, not ownerEngaged',
  },
  {
    key: 'reposts', kind: 'value', layer: 'intelligence',
    wire: 'reposts', url: 'reposts', label: 'desk_facet_reposts',
    serverOnly: true,
    note: 'how many times we have reposted it',
  },
  {
    key: 'ownerPlatforms', kind: 'value', layer: 'intelligence',
    wire: 'owner_platforms', url: 'ownerPlat', label: 'desk_facet_owner_platforms',
    serverOnly: true,
    note: 'url key is ownerPlat; counts ORIGINS, never reshares',
  },
  {
    key: 'photos', kind: 'value', layer: 'quality',
    wire: 'photos', url: 'photos', label: 'desk_facet_photos',
    serverOnly: true,
    note: 'how many photographs the advert carries',
  },
  {
    key: 'footprint', kind: 'set', layer: 'intelligence',
    wire: 'footprint', url: 'footprint', label: 'desk_facet_footprint',
    note: 'where the owner has posted; a set, not a scalar',
  },

  // -- Conditional modifiers, migration batch 5 (2026-09-03) ---------------------------------
  //
  // 🔴 A MODIFIER MEANS NOTHING ALONE, and until now that rule lived only in six separate
  // hand-written conditionals, each in a DIFFERENT layer. That is why `keepMine` slipped past a
  // check of core.js in batch 3: its conditional was not there. `requires` makes the rule data,
  // so a future filter gets it by declaring one field instead of by someone remembering.
  //
  // ⚠️ ANY-OF, and MEASURED rather than assumed. Each pair below was verified by serialising
  // the modifier with each candidate and watching whether the param appeared:
  //     currency      alone=no   priceMin=YES   priceMax=YES
  //     moveInHard    alone=no   moveInBy=YES
  //     keepMine      alone=no   exposureMax=YES
  //     dateField     alone=no   dateFrom=YES   dateTo=YES
  //     ageKinds      alone=no   ageMinDays=YES ageMaxDays=YES
  //     streetNearbyM alone=no   streetIds=YES
  {
    key: 'currency', kind: 'value', layer: 'budget',
    wire: 'currency', url: 'currency', label: 'desk_facet_currency',
    // per-m² bounds are money too (filters audit 2026-10-27 F03): without them a ppm-only search lost its currency
    requires: ['priceMin', 'priceMax', 'ppmMin', 'ppmMax'],
    // Sending it alone would needlessly route the fetch through the RPC for a no-op, and the
    // panel's currency toggle ALWAYS holds a value -- so without the requirement every search
    // would carry it and every search would look filtered.
    note: 'only alongside a price or per-m² bound',
  },
  {
    key: 'moveInHard', kind: 'flag', layer: 'operational',
    wire: 'move_in_hard', url: 'moveInHard', label: 'desk_facet_move_in_hard',
    requires: ['moveInBy'],
    note: 'a modifier with nothing to modify unless a move-in date is set',
  },
  {
    key: 'keepMine', kind: 'flag', layer: 'operational',
    wire: 'exposure_keep_mine', url: 'keepMine', label: 'desk_facet_keep_mine',
    serverOnly: true, requires: ['exposureMax'],
    // 0430/0431 -- turns the exposure filter into `team_activity <= N OR I already work this`,
    // so an agent hunting untouched leads stops losing their own worked ones (82 per agent on
    // average, 122 at worst).
    // 📌 WITHDRAWN IN BATCH 3 and restored here. It was declared, silently emitted nothing,
    // and had to be removed because its conditional is not in core.js -- the exact gap `requires`
    // closes. Its VALUE is still caller-derived server-side from the verified viewer; this flag
    // only says "apply that rule", never who "me" is.
    note: 'only alongside an exposure bound',
  },
  {
    key: 'dateField', kind: 'value', layer: 'operational',
    wire: 'date_field', url: 'dateField', label: 'desk_facet_dates',
    requires: ['dateFrom', 'dateTo'],
    // F-106 (2026-09-23): never alongside the four-dimension ranges. A restored URL could hold both,
    // and this row sent date_field next to `dates` — the pair the dateRanges note says must never travel.
    unless: f => hasDateRanges(f?.dateRanges),
    // ⚠️ Its DEFAULT is 'added', not '' -- a dimension, not a constraint. The hand-written
    // literal in ADV_DEFAULTS comes after the registry spread and still wins, so the default is
    // unchanged by declaring it here. A dimension with no range selects nothing, which is why it
    // needs the requirement even though it always holds a value.
    note: 'the dimension a date range means; default added',
  },
  {
    // B1188 (2026-09-04). FOUR DATE DIMENSIONS, EACH WITH ITS OWN RANGE, COMBINABLE.
    // Owner: "what if user wants to filter each date separately and in combinations with
    // others". `dateField`/`dateFrom`/`dateTo` above express ONE dimension with one range —
    // the single dropdown was a faithful rendering of an engine limit, and 0863 removed it.
    //
    // The value is an OBJECT, {dim: {from, to}}, which makes this the first non-scalar,
    // non-array filter in the registry. Two consequences, both declared rather than special-
    // cased downstream:
    //   • `isSet` — {} is truthy, so the generic emptiness rule would report every untouched
    //     search as filtered.
    //   • the URL carries the compact codec form, and core.js converts to JSON at the
    //     transport boundary. The generic wire loop would send "[object Object]", so core.js
    //     sets it BEFORE that loop, whose `q.has` skip then protects it.
    //
    // ⚠️ PRECEDENCE: 0863's CASE tests `p_dates is not null` FIRST, so a request carrying both
    // this and the legacy trio runs the multi-dimension branch and the legacy one is ignored.
    // That is the correct precedence, but the client must never send both, or a chip will name
    // a constraint that is not being applied. core.js enforces it.
    key: 'dateRanges', kind: 'value', layer: 'operational',
    wire: 'dates', url: 'dates', label: 'desk_facet_dates',
    isSet: hasDateRanges,
    // 🚨 B1208 (2026-09-04): the WIRE encoding lives HERE, on the entry. Without it the generic
    // transport reached this object with String() and sent the literal `dates=[object Object]`
    // on every request whose dateRanges was {} -- the server's fail-closed parse then rejected
    // the WHOLE search, and every filtered Desk search returned zero for nine hours (600+
    // rejections in search_exec_log). The urlCustom note two lines down had already named this
    // exact failure for the URL path; the wire path had the same hole and no declaration.
    wireEncode: dateRangesToWire,
    // A representative NON-EMPTY value, for the parity tests. A scalar filter needs none — any
    // non-empty string will do — but this one's shape is an object, and a test that probes it
    // with '2' proves nothing about it. Declaring the probe here keeps the tests generic and
    // keeps shape knowledge in the registry, which is the whole point of the file.
    probe: { added: { from: '2026-08-01', to: '' } },
    // The URL carries the compact codec form, not String(value) -- see urlScalarPairs.
    urlCustom: true,
    note: 'four date dimensions, each with its own range, combinable (0863)',
  },
  {
    key: 'ageKinds', kind: 'set', layer: 'intelligence',
    wire: 'age_kinds', url: 'ageKinds', label: 'desk_facet_age',
    requires: ['ageMinDays', 'ageMaxDays'],
    note: 'which dates the age bound is measured on',
  },
  {
    key: 'streetNearbyM', kind: 'value', layer: 'location',
    wire: 'streets_near', url: 'streets_near', label: 'desk_facet_street',
    requires: ['streetIds'],
    // ⚠️ The hand-written transport line carries MORE conditions than this one -- it also
    // checks the unknown-shelf paging (only on the shelf's first page, never on page 0).
    // 🔴 F-105 (2026-09-23): this used to say "the hand-written line still wins" — it did not. When
    // the hand line deliberately WITHHELD the key, the generic loop sent it anyway (its skip only
    // protects a value the hand line set), putting nearby-row hydration back on the critical request.
    // `transport: 'hand'` makes the hand line the only sender.
    transport: 'hand',
    note: 'radius around a picked street; opt-in',
  },

  // -- Batch 6 (2026-09-03): the last two single-key gaps ------------------------------------
  {
    key: 'seen', kind: 'value', layer: 'operational',
    wire: 'seen', url: 'seen', label: 'desk_facet_seen',
    values: ['seen', 'unseen', 'seen_by_me', 'unseen_by_me'],
    // 0253 -- four modes. ⚠️ IT SURVIVED THE LONE-SURVIVAL SWEEP IN BATCH 4 AND WAS STILL NOT
    // DECLARED, because the URL is not the only place a filter can be conditional: the transport
    // tests membership of this list before sending, and the derived loop would have sent whatever
    // the state held. `values` is that guard made data. A value outside the list is DROPPED, never
    // corrected to a default -- substituting one would answer a question the agent did not ask.
    // The _by_me pair carries no viewer id: the server resolves that from the verified session,
    // because a client-named viewer would let one agent ask about another's read state.
    note: 'four modes; transport validates membership',
  },
  {
    key: 'agentIds', kind: 'set', layer: 'provenance',
    wire: 'agent_ids', url: 'agent', label: 'desk_facet_agent',
    scopes: ['all'],
    // 0232 -- admin "filter by agent", pushed to SQL over the whole corpus rather than the
    // loaded pages a client-side name matcher could see. On scope=mine the party set IS the
    // agent, so the parameter would be a tautology.
    // ⚠️ DELIBERATELY NOT `serverOnly`, and the reason is recorded in deskFeed: that list means
    // "only listings can answer it", and its consequence is that clients, meetings and drafts are
    // DROPPED from the feed. The agent filter is the one server-side predicate the non-listing
    // entries CAN answer -- they carry created_by_agent_id. Adding it there once deleted every
    // client and meeting from an admin's feed; tried and reverted 2026-08-09.
    note: 'scope=all only; not serverOnly, see deskFeed',
  },

  // ── Characteristics ────────────────────────────────────────────────────────────────────────
  {
    key: 'featureStates', kind: 'set', layer: 'characteristics',
    wire: 'feature_states', url: 'feature_states', label: 'desk_facet_features',
    serverOnly: true,
    // Entries are "<code>:<yes|no|unknown>" — the restriction facet's polarity idiom, so the wire
    // carries one shape rather than two. Server-only for a STRUCTURAL reason: the feed rows carry
    // no property_feature data, so a local check would read every row as null and drop the lot —
    // the one wrong answer a filter about absence must never give.
    note: 'four-state cycle; see featureStates.js for the cycle itself',
  },
  {
    key: 'featureCodes', kind: 'set', layer: 'characteristics',
    wire: 'features', url: 'features', label: 'desk_facet_features',
    serverOnly: true,
    note: 'legacy presence-only list; featureStates supersedes it for panel use',
  },
  {
    key: 'conditionCodes', kind: 'set', layer: 'characteristics',
    wire: 'condition', url: 'condition', label: 'desk_facet_condition',
    serverOnly: true,
    // A LADDER, not a checklist: a flat sits on exactly one rung, ticks OR together, and a flat on
    // a DIFFERENT rung is an exclusion rather than an unknown. Only a flat with no rung at all
    // reaches the possible-matches shelf.
    note: 'renovation + frame rungs, OR-ed across both',
  },
  {
    key: 'conditionExcludeCodes', kind: 'set', layer: 'characteristics',
    wire: 'condition_not', url: 'condition_not', label: 'desk_facet_condition',
    serverOnly: true,
    // 0778 — THE NEGATIVE of the ladder. Owner ruling 2026-08-28: an agent can ask for NOT
    // renovated, and it is EXCLUDE / MATCH / MAY_CONSIDER, never `condition <> code` —
    // "that would quietly turn missing data into a negative". 30% of properties carry no rung.
    // ⚠️ NOT the ✕ feature state, which is where anyone would look first: every condition rung
    // carries ZERO recorded absences, so 'reno_new:no' returns nothing. A ladder's negative
    // comes from its exclusivity, which is why this is its own key and its own RPC parameter.
    // 📌 THE FIRST FILTER ADDED AFTER THE REGISTRY WAS WIRED. This row is the whole change on
    // the state side: committed, canonicalised as a set, in the URL both ways, and on the wire.
    note: 'struck rungs; unknowns shelve rather than match',
  },

  // ── Provenance ─────────────────────────────────────────────────────────────────────────────
  // `sources` (the portal filter, still hand-written in IntakeTab) is this layer's other member.
  // ⚠️ The layer here says 'provenance' and NOT 'intelligence', which was the first answer: the
  // facet renders immediately after წყარო, and a registry that files a filter under one layer
  // while the panel draws it in another is the drift this file exists to stop. If the grammar
  // ever consumes `layer` for ordering, these two must move together.
  {
    key: 'addedHow', kind: 'value', layer: 'provenance',
    wire: 'added_how', url: 'added_how', label: 'desk_facet_added_how',
    serverOnly: true,
    // 0779/0780 — HOW IT ENTERED OUR BOOK, which is not where the advert is LISTED. Owner asked
    // for both in one breath and only the first existed: `sources` reads listing.source (the
    // portal), this reads listing_intake (the first capture).
    // Measured: crawler discovery 63,841 listings · agent extension 18,780.
    // ⚠️ FIRST capture. An agent re-scraping something the crawler already found is not an
    // agent-added listing — 1,599 of them — and a "what did my people bring in" filter that
    // credits the crawler's finds to an agent is worse than no filter.
    note: 'crawler | agent; any member of the cluster, like sources',
  },
  {
    key: 'mapPolygon', kind: 'value', layer: 'location',
    wire: 'polygon', url: 'polygon', label: 'desk_facet_map_area',
    serverOnly: true,
    // 🗺️ A SHAPE DRAWN ON THE MAP. The demand side has had one since 0041
    // (requirement_criteria.search_polygon) and AreaPreview has been rendering it for a year;
    // listing search could never ask "inside this shape" because no server filter took one.
    //
    // 🔑 STORED AS A STRING — "lat,lng;lat,lng;…" — AND THAT IS THE WHOLE REASON IT IS HERE.
    // A ring is structured, and structured filters in this Desk have always been hand-wired:
    // `near` is, and it costs a bespoke encoder in deskUrl, a bespoke branch in
    // canonicalFilters, its own entry in every committed list, and its own chip. Nine sites,
    // nine chances to produce the half-existing key this file exists to prevent.
    // Flattening the ring to a scalar makes it an ORDINARY `value` filter, so defaults,
    // committed-ness, both halves of the URL, the request and the server-only test are all
    // DERIVED. The map component owns the parsing; nothing else needs to know the shape.
    // ⚠️ Six decimal places, set at the point of capture: ~11 cm, far finer than any pin we
    // hold, and enough to keep a URL of a dozen vertices short enough to share.
    note: 'ring as lat,lng;lat,lng;… — the map draws it, the RPC clips to it',
  },
  // ── B1213 step 8 (2026-09-06): the EXCLUDE halves, derived from the single filter definition ────────
  // One row per categorical include row that declares `exclude` in core/search-filter-spec.json — the
  // state key, URL key and wire key are the spec's, so a facet's ✕ is committed, canonicalised as a set,
  // written to the URL, parsed back and sent without a second hand-written row anywhere. The include row
  // keeps its label; this one is the same facet's other half (facetTriState.js cycles between them).
  ...excludeRowsFromSpec(),

]

// 🔴 F-107 (2026-09-23): A ✕ HALF OBEYS THE SAME GATES AS ITS ✓ HALF. The generated rows above copied
// no `scopes` and no `requires`, so agentIdsNot travelled outside scope `all` (the ✓ half never does)
// and ageKindsNot travelled with no age bound (the ✓ half needs one — alone it measures nothing).
// Inherited here, after the array exists, rather than inside excludeRowsFromSpec, which runs while
// DESK_FILTERS is still being built and cannot see the ✓ rows.
for (const row of DESK_FILTERS) {
  if (!row.excludeOf) continue
  const include = DESK_FILTERS.find(r => r.key === row.excludeOf)
  if (!include) continue
  if (include.scopes && !row.scopes) row.scopes = include.scopes
  if (include.requires && !row.requires) row.requires = include.requires
}

// ── the drawn-area ring, to and from its wire form ───────────────────────────────────────────
// "lat,lng;lat,lng;…" at six decimals. It lives HERE, beside the `mapPolygon` entry that declares
// the format, and not in the component that draws it — for the reason locDistM was moved to the
// pure feed module: a codec inside a .jsx file cannot be imported by `node --test`, so the one
// piece of this filter with real edge cases would be the one piece nothing could test.
//
// ⚠️ BOTH DIRECTIONS ARE TOTAL. Anything unparseable yields null rather than a partial ring,
// because half a polygon is not a smaller search area — it is a DIFFERENT one, silently. A
// truncated ring still closes itself into a valid shape, so it would narrow the results to a
// region the agent never drew and never see an error.
// ⚠️ Six decimals ≈ 11 cm, finer than any pin we hold, and short enough that a dozen vertices
// still fit comfortably in a shareable URL.
export function ringToParam(ring) {
  if (!Array.isArray(ring) || ring.length < 3) return ''
  if (!ring.every(pt => Array.isArray(pt) && pt.length === 2 && pt.every(n => Number.isFinite(Number(n))))) return ''
  return ring.map(([lat, lng]) => `${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`).join(';')
}

export function ringFromParam(str) {
  if (!str || typeof str !== 'string') return null
  const parts = str.split(';')
  if (parts.length < 3) return null
  const ring = parts.map(pair => {
    const bits = pair.split(',')
    if (bits.length !== 2) return null
    const lat = Number(bits[0]), lng = Number(bits[1])
    // A coordinate outside the planet is not a rounding slip, it is a malformed link.
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null
    return [lat, lng]
  })
  return ring.every(Boolean) ? ring : null
}

// Multiple map areas travel through the existing scalar filter as rings separated by `|`.
// `|` is deliberately outside the coordinate grammar, so an old one-ring URL remains valid
// and no coordinate can accidentally split into two areas. A malformed member invalidates the
// whole value: silently dropping one area would show a broader search than the user saved.
export function ringsToParam(rings) {
  if (!Array.isArray(rings) || !rings.length) return ''
  const encoded = rings.map(ringToParam)
  return encoded.every(Boolean) ? encoded.join('|') : ''
}

export function ringsFromParam(str) {
  if (!str || typeof str !== 'string') return []
  const rings = str.split('|').map(ringFromParam)
  return rings.every(Boolean) ? rings : []
}

// ── the four date ranges, to and from their wire form ────────────────────────────────────────
// B1188. "added:2026-08-01..2026-08-31;posted:..2026-07-01" — dimension, colon, from, '..', to.
// Either side may be empty; a dimension with neither is not written at all.
//
// It lives HERE beside the ring codecs above, for the reason stated there: a codec inside a
// .jsx cannot be imported by node --test, so the one part of this filter with real edge cases
// would be the one part nothing could test.
//
// 🔑 WHY A COMPACT STRING AND NOT JSON. The server takes JSON (dates={"added":{"from":…}}),
// but a URL is read and shared by people: JSON in a hash is unreadable, and every brace, quote
// and colon becomes percent-encoded noise. The OBJECT is the state, THIS is the URL, and
// core.js converts to JSON at the transport boundary — one codec, two consumers, no second
// grammar to keep in step.
//
// ⚠️ BOTH DIRECTIONS ARE TOTAL, like the ring codec. Anything unparseable yields {} rather than
// a partial set, because half a date filter is not a narrower search — it is a DIFFERENT one,
// silently, and it would show more rows than the agent asked for while a chip claimed otherwise.
export const DATE_DIMS = ['added', 'posted', 'posted_or_added', 'last_seen', 'updated']
// An explicit class, not a shorthand: a backslash escape here was silently lost once
// already, and JavaScript regex shorthands are ASCII-only anyway (CLAUDE.md).
const ISO_DATE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/

export function dateRangesToParam (obj) {
  if (!obj || typeof obj !== 'object') return ''
  const out = []
  for (const dim of DATE_DIMS) {
    const r = obj[dim]
    if (!r) continue
    const from = (r.from || ''), to = (r.to || '')
    if (!from && !to) continue
    if ((from && !ISO_DATE.test(from)) || (to && !ISO_DATE.test(to))) return ''
    out.push(dim + ':' + from + '..' + to)
  }
  return out.join(';')
}

/**
 * The WIRE form: the JSON object the server registry's `dates` parser accepts
 * ({dim: {from?, to?}}, ISO dates only). '' when nothing is set, so the transport sends nothing.
 * Kept beside the URL codec on purpose -- two encodings of one value, declared in one place.
 */
export function dateRangesToWire (obj) {
  if (!hasDateRanges(obj)) return ''
  const out = {}
  for (const dim of DATE_DIMS) {
    const r = obj[dim]
    if (!r || (!r.from && !r.to)) continue
    if ((r.from && !ISO_DATE.test(r.from)) || (r.to && !ISO_DATE.test(r.to))) return ''
    out[dim] = {}
    if (r.from) out[dim].from = r.from
    if (r.to) out[dim].to = r.to
  }
  return Object.keys(out).length ? JSON.stringify(out) : ''
}

export function dateRangesFromParam (str) {
  if (!str || typeof str !== 'string') return {}
  const out = {}
  for (const part of str.split(';')) {
    if (!part) continue
    const c = part.indexOf(':')
    if (c < 0) return {}
    const dim = part.slice(0, c)
    if (!DATE_DIMS.includes(dim)) return {}
    const rest = part.slice(c + 1)
    const d = rest.indexOf('..')
    if (d < 0) return {}
    const from = rest.slice(0, d), to = rest.slice(d + 2)
    if ((from && !ISO_DATE.test(from)) || (to && !ISO_DATE.test(to))) return {}
    if (!from && !to) return {}
    // ⚠️ from > to is a range that can never match. It is KEPT, not repaired: the agent typed
    // it, the panel shows it, and silently swapping the bounds would answer a question nobody
    // asked. The RPC returns 0 for it, which is the honest answer.
    out[dim] = { from: from || '', to: to || '' }
  }
  return out
}

// Is any dimension actually constrained? An object of empty ranges is not a filter, and the
// difference decides whether the request goes down the filtered path at all.
export function hasDateRanges (obj) {
  return !!obj && typeof obj === 'object' &&
    DATE_DIMS.some(d => obj[d] && (obj[d].from || obj[d].to))
}

const byKind = (k) => DESK_FILTERS.filter(f => f.kind === k)
const pair = (v) => Array.isArray(v) ? v : [v]

/** Every state key a filter owns. A range owns two. */
export function keysOf(f) {
  return f.kind === 'range' ? [f.key + 'Min', f.key + 'Max'] : [f.key]
}

/** The initial value for each key — merge into ADV_DEFAULTS. */
export function defaults() {
  const out = {}
  for (const f of DESK_FILTERS)
    for (const k of keysOf(f)) out[k] = f.kind === 'set' ? [] : f.kind === 'flag' ? false : ''
  return out
}

/** Keys with array semantics — the list canonicalFilters and the URL parser both need. */
export function arrayKeys() {
  return byKind('set').map(f => f.key)
}

/**
 * Keys that survive a commit, a refresh and a share.
 * ⚠️ THE LIST A HAND-WRITTEN FILTER FORGETS. Absent from it, a filter narrows the live request
 * and is stripped on restore while its chip still claims it — recorded four times, twice today.
 */
export function committedKeys() {
  return DESK_FILTERS.flatMap(keysOf)
}

/** [stateKey, urlKey] for every key — used for BOTH directions, so they cannot disagree. */
export function urlPairs() {
  const out = []
  for (const f of DESK_FILTERS) {
    const ks = keysOf(f), us = pair(f.url)
    if (ks.length !== us.length)
      throw new Error(`deskFilters: ${f.key} declares ${ks.length} keys and ${us.length} url keys`)
    ks.forEach((k, i) => out.push([k, us[i]]))
  }
  return out
}

/** [stateKey, queryParam] for every key that travels. `wire: null` means deliberately local. */
export function wire() {
  const out = []
  for (const f of DESK_FILTERS) {
    if (f.wire === null) continue
    const ks = keysOf(f), ws = pair(f.wire)
    if (ks.length !== ws.length)
      throw new Error(`deskFilters: ${f.key} declares ${ks.length} keys and ${ws.length} wire keys`)
    ks.forEach((k, i) => out.push([k, ws[i]]))
  }
  return out
}

/** Keys the loaded feed cannot answer locally — it must go to the server or not filter at all. */
export function serverOnlyKeys() {
  return DESK_FILTERS.filter(f => f.serverOnly).flatMap(keysOf)
}

/**
 * Mins where 0 is VACUOUS rather than a real bound. The fact is null on most listings, so
 * "from 0" reads as a filter and shelves the corpus — caught live on bathrooms in 2026-07-11.
 */
export function vacuousZeroMins() {
  return DESK_FILTERS.filter(f => f.kind === 'range' && f.vacuousZeroMin).map(f => f.key + 'Min')
}

/**
 * Is any registered filter non-default in this filter set?
 * ⚠️ A MODIFIER WHOSE REQUIREMENT IS UNMET IS NOT ACTIVE. `dateField` defaults to 'added',
 * not '', so without this guard an untouched search reports filters active and the feed takes
 * the filtered path for a no-op. Caught by 34 feed-pipeline tests the moment dateField was
 * declared -- a default that is not the empty value is invisible until something asks
 * "is anything set?".
 */
// Emptiness, spelled the ordinary way. A filter whose value is not a scalar or an array
// declares its own `isSet` instead — see `dateRanges`, whose default is {} and which is
// TRUTHY, so the rule below would call an untouched search filtered. That is not hypothetical:
// declaring `dateField` with a non-empty default broke 34 feed-pipeline tests for exactly this
// reason, and the fix then was `requires`. This is the same lesson for a non-scalar shape.
const isSetDefault = (v) => Array.isArray(v) ? v.length > 0 : v !== '' && v != null && v !== false

export function activeIn(filters) {
  if (!filters) return false
  return DESK_FILTERS.some(f => requirementMet(filters, f.key) &&
    keysOf(f).some(k => (f.isSet || isSetDefault)(filters[k])))
}

/**
 * Range bounds that are SET but are not a number (search/filter audit 2026-10-23, B05).
 * The wire copies a bound verbatim and the server reads it with parseFloat: "abc" became null, the
 * same as no bound, so the search silently ran without it; "1200garbage" became 1200. The panel's
 * controls only write numbers, so these arrive from a hand-edited or old link or a saved search.
 * A set-but-invalid bound is neither absent nor a number: the Desk shows it and refuses to search
 * with it (core.js) instead of answering with the wider corpus. Strict: optional sign, digits, one
 * optional decimal point, nothing else. Returns [{ key, value, label }] in registry order.
 */
const STRICT_NUMBER = /^\s*[-+]?(?:\d+(?:\.\d*)?|\.\d+)\s*$/
export function isStrictNumber(v) {
  if (typeof v === 'number') return Number.isFinite(v)
  return typeof v === 'string' && STRICT_NUMBER.test(v) && Number.isFinite(Number(v))
}
export function invalidNumericBounds(filters) {
  const out = []
  if (!filters) return out
  for (const f of DESK_FILTERS) {
    if (f.kind !== 'range') continue
    for (const k of keysOf(f)) {
      const v = filters[k]
      if (v === '' || v == null) continue
      if (!isStrictNumber(v)) out.push({ key: k, value: v, label: f.label })
    }
  }
  return out
}

/** Look one up. */
export function filterFor(key) {
  return DESK_FILTERS.find(f => keysOf(f).includes(key)) || null
}

/**
 * [stateKey, urlKey] split by how the URL carries the value.
 *
 * ⚠️ THE SPLIT IS THE SERIALIZATION, and getting it wrong is silent. A `set` travels as one
 * comma-joined param and must come back as an ARRAY; a range or scalar is copied verbatim as a
 * string. Hand a set to the scalar path and it round-trips as the literal text "a,b" — one
 * "filter" nothing matches, with no error anywhere.
 */
export function urlSetPairs() {
  return DESK_FILTERS.filter(f => f.kind === 'set').map(f => [f.key, pair(f.url)[0]])
}
/**
 * [stateKey, urlParam] for every FLAG.
 * 🔴 FLAGS ARE THEIR OWN PATH AND THIS IS NOT TIDINESS. A boolean handed to the scalar path
 * serialises as the literal "false" -- deskUrl's own comment has warned about it since the file
 * was written -- and the parse side tests `=== '1'`. So an unset flag would write `topFloor=false`
 * into the URL, the hand-written `put('topFloor','1')` would push a SECOND copy, parse would take
 * the first, and the filter would be silently lost on restore. A flag writes '1' or nothing.
 */
/**
 * Is a filter's REQUIREMENT satisfied by this filter set?
 *
 * 🔴 SOME FILTERS ARE MODIFIERS AND MEAN NOTHING ALONE. `currency` without a price bound,
 * `moveInHard` without a move-in date, `keepMine` without an exposure bound, `dateField` without
 * a date range, `ageKinds` without an age bound, `streetNearbyM` without a street. Each was
 * hand-written with its own conditional, and each conditional lived in a DIFFERENT layer -- which
 * is why `keepMine` slipped past a check of core.js in batch 3 and had to be withdrawn.
 *
 * `requires` is ANY-OF, not all-of, and that is measured rather than assumed: `currency` appears
 * with priceMin OR priceMax, `dateField` with dateFrom OR dateTo, `ageKinds` with ageMinDays OR
 * ageMaxDays. Verified by serialising each pair.
 *
 * An entry with no `requires` is always satisfied, so every existing filter is unaffected.
 */
export function requirementMet(filters, key) {
  const f = DESK_FILTERS.find(e => keysOf(e).includes(key))
  if (!f || !f.requires) return true
  return f.requires.some(k => {
    const v = filters?.[k]
    return Array.isArray(v) ? v.length > 0 : (v !== '' && v != null && v !== false)
  })
}

/**
 * May this filter travel on the wire, for this filter set and this feed scope?
 *
 * Three declared constraints, each of which existed as a hand-written conditional first:
 *   requires  a MODIFIER needs the thing it modifies      (batch 5)
 *   values    a VALUE must be one the server understands  (`seen`, four modes)
 *   scopes    a filter that only means something in some feed scopes (`agentIds`, scope=all)
 *
 * ⚠️ `values` is a TRANSPORT guard, not a UI one. It exists because the derived loop would
 * otherwise send whatever the state happened to hold, where the hand-written line tested
 * membership first. A value outside the list is dropped rather than corrected -- silently
 * substituting a default would answer a question the agent did not ask.
 *
 * ⚠️ `scopes` cannot live in `requires`: it constrains on the FEED SCOPE, which is not a
 * filter key and is not in the filter set at all. Conflating them would have meant inventing a
 * fake filter key to hang it on.
 */
export function transportAllowed(filters, key, scope) {
  const f = DESK_FILTERS.find(e => keysOf(e).includes(key))
  if (!f) return true
  // F-105: a HAND-OWNED wire key is sent only by its hand-written line in searchWire.js, which carries
  // conditions a row cannot express. The generic loop's `q.has` skip protected only a value the hand
  // line DID send; when the hand line deliberately withheld it, the loop sent it anyway.
  if (f.transport === 'hand') return false
  if (!requirementMet(filters, key)) return false
  // F-106: a row may be suppressed by another filter's presence (the legacy date trio when dateRanges
  // is set — 0863 runs p_dates first, so sending both names a constraint that is not applied).
  if (typeof f.unless === 'function' && f.unless(filters)) return false
  if (f.scopes && !f.scopes.includes(scope)) return false
  if (f.values && !f.values.includes(filters?.[key])) return false
  return true
}

/**
 * ONE value's wire form, or '' when it must not travel. Pure, so it is testable without a browser.
 *   set   -> CSV (nothing when empty)          flag -> '1' (nothing when false)
 *   value -> String(scalar); an OBJECT must declare `wireEncode` on its entry -- there is no
 *            sensible default for an object, and String() of one is the literal "[object Object]",
 *            which the server rejects and which took every filtered search down on 2026-09-04.
 * ⚠️ REFUSES LOUDLY. An object without an encoder throws at the call, in the test suite, before
 * it can reach a request. A silent skip here would be the same bug wearing a quieter face.
 */
export function encodeWireValue (entry, v) {
  if (v == null || v === '' || v === false) return ''
  if (entry?.isSet && !entry.isSet(v)) return ''
  if (Array.isArray(v)) return v.length ? v.join(',') : ''
  if (v === true) return '1'
  if (typeof v === 'object') {
    if (typeof entry?.wireEncode !== 'function')
      throw new Error(`deskFilters: ${entry?.key} carries an object but declares no wireEncode`)
    const enc = entry.wireEncode(v)
    if (typeof enc !== 'string') throw new Error(`deskFilters: ${entry.key}.wireEncode must return a string`)
    return enc
  }
  return String(v)
}

/**
 * Every [queryParam, value] the registry says this filter state sends, in registry order.
 * The transport (src/db/core.js) sets these after its hand-written lines and never overwrites
 * one it already set. Requirements (`requires`), scopes and enumerated `values` are honoured
 * through transportAllowed, exactly as before.
 */
export function wireParams (filters, scope) {
  const out = []
  for (const f of DESK_FILTERS) {
    if (f.wire === null) continue
    const ks = keysOf(f), ws = pair(f.wire)
    ks.forEach((k, i) => {
      if (!transportAllowed(filters, k, scope)) return
      const enc = encodeWireValue(f, filters?.[k])
      if (enc !== '') out.push([ws[i], enc])
    })
  }
  return out
}

export function urlFlagPairs() {
  return DESK_FILTERS.filter(f => f.kind === 'flag').map(f => [f.key, pair(f.url)[0]])
}
// ⚠️ `urlCustom` opts a filter OUT of the verbatim-scalar path. F2P copies a value into the URL
// with String(), which is right for every scalar and silently wrong for anything else: an
// object arrives as the literal [object Object], which then round-trips as that text and
// becomes one filter nothing matches, with no error anywhere. A filter that needs a codec
// declares it here and hand-writes both halves in deskUrl.js beside the ring.
export function urlScalarPairs() {
  return DESK_FILTERS.filter(f => f.kind !== 'set' && f.kind !== 'flag' && !f.urlCustom).flatMap(f => {
    const ks = keysOf(f), us = pair(f.url)
    return ks.map((k, i) => [k, us[i]])
  })
}
